import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { RailArrow, useRailScroller } from "@/components/RailArrows";
import { useCity } from "@/contexts/CityContext";
import { track } from "@/lib/analytics";
import {
  coverImageUrl,
  fetchProgramming,
  focalPosition,
  formatTime,
  imageSrcSet,
  movieHref,
  sizedImageUrl,
  type ScreeningCard,
} from "@/lib/queries";
import { cinemaTone } from "@/lib/cinemaTone";
import { cn } from "@/lib/utils";
import { cityForName, cityPath, madridMinutesNow, madridToday } from "@/seo/shared";

/**
 * «Hoy en {ciudad}» (28-10-2026): lo que ponen hoy las salas, por hora, en
 * la portada. Es el motivo para volver cada día; el patrón es el
 * «{Ciudad} Hoy» de RE:PÚBLICO sin su curación editorial: aquí manda la
 * hora del próximo pase y lo ya empezado (hace más de 15 min) va al final,
 * atenuado. Misma consulta y caché que la cartelera. Las tarjetas miden lo
 * mismo que las de los rails de debajo (29-10-2026, usuario: más pequeñas
 * dejaban un aire que distraía).
 */
type TodayMovie = {
  key: string;
  movie: NonNullable<ScreeningCard["movies"]> & { id: string };
  city: string;
  cinemas: string[];
  times: string[];
  nextTime: string | null;
  started: boolean;
};

const MAX_CARDS = 12;

export function TodayInCity() {
  const { t } = useTranslation();
  const { city } = useCity();
  const { data, isLoading } = useQuery<ScreeningCard[]>({
    queryKey: ["screenings", "programming"],
    queryFn: fetchProgramming,
    staleTime: 60_000,
    enabled: Boolean(city),
  });

  const items = useMemo<TodayMovie[]>(() => {
    if (!city || !data) return [];
    const today = madridToday();
    const cutoff = madridMinutesNow() - 15;
    const byMovie = new Map<string, TodayMovie>();
    for (const s of data) {
      if (s.screening_date !== today || s.cinemas?.city !== city || !s.movies) continue;
      const [h, m] = s.screening_time.split(":").map(Number);
      const minutes = h * 60 + m;
      let item = byMovie.get(s.movie_id);
      if (!item) {
        item = {
          key: s.movie_id,
          movie: { ...s.movies, id: s.movie_id },
          city,
          cinemas: [],
          times: [],
          nextTime: null,
          started: true,
        };
        byMovie.set(s.movie_id, item);
      }
      if (s.cinemas?.name && !item.cinemas.includes(s.cinemas.name)) item.cinemas.push(s.cinemas.name);
      item.times.push(s.screening_time);
      if (minutes >= cutoff) {
        item.started = false;
        if (!item.nextTime || s.screening_time < item.nextTime) item.nextTime = s.screening_time;
      }
    }
    return [...byMovie.values()]
      .map((i) => ({ ...i, times: [...new Set(i.times)].sort() }))
      .sort((a, b) => {
        if (a.started !== b.started) return a.started ? 1 : -1;
        return (a.nextTime ?? a.times[a.times.length - 1]).localeCompare(b.nextTime ?? b.times[b.times.length - 1]);
      })
      .slice(0, MAX_CARDS);
  }, [city, data]);

  const { scrollerRef, canLeft, canRight, nudge } = useRailScroller(items.length);

  if (!city || (!isLoading && items.length === 0)) return null;
  const cityDef = cityForName(city);

  return (
    <section className="py-14 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.9] tracking-[-0.03em]">
              {t("landing.today.title", { city })}
            </h2>
            <p className="data-voice text-[11px] font-bold text-rc-navy/70 mt-2">{t("landing.today.subtitle")}</p>
          </div>
          {cityDef && (
            <Link
              to={cityPath(cityDef.slug)}
              className="text-sm font-semibold text-rc-navy underline underline-offset-4 hover:text-rc-navy/70 transition-colors"
            >
              {t("landing.today.all")}
            </Link>
          )}
        </div>

        {isLoading ? (
          <div className="flex gap-5 overflow-hidden">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="shrink-0 w-[216px] sm:w-[240px] md:w-[264px] lg:w-card aspect-[2/3] bg-rc-navy/5 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="relative">
            {/* Flechas al centro del póster (la tarjeta lleva el título debajo). */}
            <RailArrow dir="left" show={canLeft} onClick={() => nudge(-1)} className="top-[calc(50%-1.75rem)]" />
            <RailArrow dir="right" show={canRight} onClick={() => nudge(1)} className="top-[calc(50%-1.75rem)]" />
            <div
              ref={scrollerRef}
              className="flex gap-5 overflow-x-auto pb-2 snap-x snap-proximity overscroll-x-contain [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            >
              {items.map((item) => (
                <TodayCard key={item.key} item={item} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function TodayCard({ item }: { item: TodayMovie }) {
  const { t } = useTranslation();
  const cover = coverImageUrl({ movie_images: item.movie.movie_images });
  const times = item.times.slice(0, 3).map(formatTime).join(" · ");
  const extra = item.times.length - 3;
  const line =
    item.cinemas.length === 1
      ? `${item.cinemas[0]} · ${times}${extra > 0 ? ` +${extra}` : ""}`
      : `${t("landing.today.cinemas", { count: item.cinemas.length })} · ${t("landing.today.sessions", { count: item.times.length })}`;

  return (
    <Link
      to={movieHref(item.movie, item.city)}
      onClick={() => track("movie_click", { page_type: "home", source: "today", movie: item.movie.title, city: item.city })}
      className={cn(
        "group block shrink-0 snap-start w-[216px] sm:w-[240px] md:w-[264px] lg:w-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-violet focus-visible:ring-offset-2",
        item.started && "opacity-60"
      )}
    >
      {/* Exploración: la foto en su color (cada tarjeta es una sesión que se
          elige, como en la cartelera); el color del cine va en la hora. */}
      <div className="relative aspect-[2/3] overflow-hidden bg-rc-navy">
        <img
          src={sizedImageUrl(cover, { width: 528, height: 792, focal: item.movie })}
          srcSet={imageSrcSet(cover, [264, 528, 792], 1.5, item.movie)}
          sizes="(max-width: 640px) 216px, (max-width: 768px) 240px, (max-width: 1024px) 264px, 299px"
          alt=""
          loading="lazy"
          style={{ objectPosition: focalPosition(item.movie) }}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 ease-spring group-hover:scale-[1.04]"
        />
        {item.started && (
          <span className="data-voice absolute left-3 top-3 rounded-full bg-white px-2.5 py-1 text-[10px] font-bold text-rc-navy">
            {t("landing.today.started")}
          </span>
        )}
        {!item.started && item.nextTime && (
          <span className={cn("absolute left-3 top-3 px-2 py-0.5 font-display text-xl leading-tight tabular-nums", cinemaTone(item.cinemas[0]).bg, cinemaTone(item.cinemas[0]).fg)}>
            {formatTime(item.nextTime)}
          </span>
        )}
      </div>
      <div className="mt-2.5 font-display text-xl uppercase leading-[0.95] tracking-[-0.01em] line-clamp-2 text-rc-navy">{item.movie.title}</div>
      <div className="data-voice mt-1.5 text-[10px] font-bold text-rc-navy/70 line-clamp-1">{line}</div>
    </Link>
  );
}
